import { OperatorRecord, SegmentData, StopBlock } from "../types";
import { flattenStopBlocks } from "./mapHelpers";

/** Valid EnumOperatorType values for atlas type-filters. */
export const VALID_BUSINESS_TYPES = [
  "ACCOMMODATION",
  "ATTRACTION",
  "FESTIVALS_AND_EVENTS",
  "FOOD_AND_BEVERAGE_BUSINESS_TYPE",
  "GOVERNMENT",
  "OUTDOOR_RECREATION",
  "RETAIL",
  "SERVICE",
  "TOURISM_ORGANIZATION",
  "TOURISM_SUPPORT_OR_ADJACENT",
  "TOURS",
  "TRANSPORTATION",
  "VENUE",
  "OTHER_BUSINESS_TYPE",
] as const;

export type ValidBusinessType = (typeof VALID_BUSINESS_TYPES)[number];

const VALID_BUSINESS_TYPE_SET = new Set<string>(VALID_BUSINESS_TYPES);

export const BUSINESS_TYPE_LABELS: Record<ValidBusinessType, string> = {
  ACCOMMODATION: "Accommodation",
  ATTRACTION: "Attraction",
  FESTIVALS_AND_EVENTS: "Events",
  FOOD_AND_BEVERAGE_BUSINESS_TYPE: "Food & Beverage",
  GOVERNMENT: "Government",
  OUTDOOR_RECREATION: "Outdoor Rec",
  RETAIL: "Retail",
  SERVICE: "Service",
  TOURISM_ORGANIZATION: "Tourism Org",
  TOURISM_SUPPORT_OR_ADJACENT: "Tourism Support",
  TOURS: "Tours",
  TRANSPORTATION: "Transportation",
  VENUE: "Venue",
  OTHER_BUSINESS_TYPE: "Other",
};

/** null = All types active; non-empty array = OR filter on operator.type */
export type BusinessTypeFilterSelection = string[] | null;

/** Whether the embed enables atlas business-type filter chips. */
export function isBusinessTypeFiltersEnabled(value: string | undefined): boolean {
  return value === "true";
}

/** Distinct operator types present in the itinerary, in canonical display order. */
export function getBusinessTypesInItinerary(
  segments: SegmentData[],
  operatorByEntityId: Record<string, OperatorRecord>,
): ValidBusinessType[] {
  const types = new Set<string>();
  for (const stop of flattenStopBlocks(segments)) {
    const type = getOperatorBusinessType(stop, operatorByEntityId);
    if (type && VALID_BUSINESS_TYPE_SET.has(type)) {
      types.add(type);
    }
  }
  return VALID_BUSINESS_TYPES.filter(type => types.has(type));
}

export function businessTypesToKey(types: ValidBusinessType[]): string {
  return types.join("|");
}

export function getBusinessTypeLabel(type: string): string {
  return BUSINESS_TYPE_LABELS[type as ValidBusinessType] ?? type
    .replace(/_/g, " ")
    .replace(/business type/i, "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function isShowingAllBusinessTypes(selection: BusinessTypeFilterSelection): boolean {
  return selection === null || selection.length === 0;
}

export function getOperatorBusinessType(
  stop: StopBlock,
  operatorByEntityId: Record<string, OperatorRecord>,
): string | null {
  const entityId = stop.entityRef?.entityId?.toString();
  if (!entityId) return null;
  return operatorByEntityId[entityId]?.type ?? null;
}

export function isStopVisibleForBusinessTypeFilter(
  stop: StopBlock,
  operatorByEntityId: Record<string, OperatorRecord>,
  selection: BusinessTypeFilterSelection,
): boolean {
  if (isShowingAllBusinessTypes(selection)) return true;

  const type = getOperatorBusinessType(stop, operatorByEntityId);
  if (!type) return false;
  return selection!.includes(type);
}

export function selectionToKey(selection: BusinessTypeFilterSelection): string {
  if (isShowingAllBusinessTypes(selection)) return "all";
  return [...selection!].sort().join("|");
}

export function toggleBusinessTypeFilterSelection(
  selection: BusinessTypeFilterSelection,
  type: string | "all",
): BusinessTypeFilterSelection {
  if (type === "all") return null;

  if (isShowingAllBusinessTypes(selection)) {
    return [type];
  }

  const activeSelection = selection!;

  if (activeSelection.includes(type)) {
    const next = activeSelection.filter(value => value !== type);
    return next.length === 0 ? null : next;
  }

  return [...activeSelection, type];
}

export function isBusinessTypeFilterActive(
  selection: BusinessTypeFilterSelection,
  type: string | "all",
): boolean {
  if (type === "all") return isShowingAllBusinessTypes(selection);
  return !isShowingAllBusinessTypes(selection) && selection!.includes(type);
}
